import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LegalDisclaimer } from '../../components/layout/LegalDisclaimer';
import { TopNav } from '../../components/layout/TopNav';
import {
  ageSelectionLabel,
  ALL_AGES_ID,
  bracketsFromAgeIds,
  STATE_FIPS_TO_NAME,
} from '../../config/dropdowns';
import { heroMasked } from '../../assets/figma';
import { ApiError } from '../../services/apiClient';
import { amountToDollars } from '../impact/data/impactTypes';
import { getAreaName } from '../impact/services/impactService';
import { fetchImpactResult } from '../impact/services/impactApi';
import type { GeoLevel } from '../impact/types';

import { SummaryBar } from './components/SummaryBar';
import { createLead } from './services/leadsApi';
import { connectPageStyles as styles } from './ConnectPage.styles';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const digitsOnly = (value: string) => value.replace(/\D/g, '');
const groupUsd = (digits: string) =>
  digits ? Number(digits).toLocaleString('en-US') : '';

/** Progressively format up to 10 digits as a US number: (555) 000-0000. */
const formatUsPhone = (value: string): string => {
  const d = digitsOnly(value).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** 10 raw digits → the "+1 617 555 0100" shape the /leads API expects. */
const toE164Display = (tenDigits: string): string =>
  `+1 ${tenDigits.slice(0, 3)} ${tenDigits.slice(3, 6)} ${tenDigits.slice(6)}`;

export function ConnectPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const level = (params.get('level') as GeoLevel) || 'state';
  const areaFips = useMemo(
    () =>
      (params.get('areas') || params.get('location') || '')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean),
    [params],
  );
  const amountDollars = useMemo(() => amountToDollars(params.get('amount')), [params]);
  const ageIds = useMemo(
    () => (params.get('age') || ALL_AGES_ID).split(',').filter(Boolean),
    [params],
  );
  const ageLabel = useMemo(() => ageSelectionLabel(ageIds), [ageIds]);
  const brackets = useMemo(() => bracketsFromAgeIds(ageIds), [ageIds]);

  // Area names shown immediately from the live CDN dataset (real data, no
  // fabrication) while /impact/result is in flight; its response's own
  // `areas[].name` takes over once it resolves.
  const liveAreaNames = useMemo(
    () => areaFips.map((fips) => getAreaName(level, fips) ?? STATE_FIPS_TO_NAME[fips] ?? fips),
    [areaFips, level],
  );

  // reachChildren/wealthSavings come ONLY from /impact/result — no local
  // computation. They simply don't render (SummaryBar hides zero/blank
  // values) until the real response arrives.
  const [apiSummary, setApiSummary] = useState<{
    areaNames: string[];
    reachChildren: number;
    totalWealthSavings: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchImpactResult({
      level,
      fips: areaFips,
      amountDollars,
      brackets,
    })
      .then((result) => {
        if (cancelled) return;
        setApiSummary({
          areaNames: result.areas.map((a) => a.name),
          reachChildren: result.reachChildren,
          totalWealthSavings: result.totalWealthSavings,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('[ConnectPage] /impact/result failed:', err);
        setApiSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [level, areaFips, amountDollars, brackets]);

  const summary = {
    amountDollars,
    areaNames: apiSummary?.areaNames ?? liveAreaNames,
    ageLabel,
    reachChildren: apiSummary?.reachChildren ?? 0,
    wealthSavings: apiSummary?.totalWealthSavings ?? 0,
  };

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState(() => groupUsd(String(summary.amountDollars)));
  const [agreed, setAgreed] = useState(false);

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const phoneDigits = digitsOnly(phone);
  const amountDigits = digitsOnly(amount);

  const errors = {
    firstName: firstName.trim().length < 2 ? 'Enter at least 2 characters' : '',
    lastName: lastName.trim().length < 2 ? 'Enter at least 2 characters' : '',
    email: !EMAIL_RE.test(email.trim())
      ? 'Enter a valid email, e.g. name@company.com'
      : '',
    phone:
      phoneDigits.length > 0 && phoneDigits.length !== 10
        ? 'US number must be 10 digits'
        : '',
    amount: !amountDigits || Number(amountDigits) <= 0 ? 'Enter a gift amount' : '',
    agreed: !agreed ? 'Please agree to our privacy policy to continue' : '',
  };
  const isValid = Object.values(errors).every((e) => !e);

  const showError = (field: keyof typeof errors) =>
    (touched[field] || submitted) && errors[field] ? errors[field] : '';

  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!isValid || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await createLead({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        donationAmount: Number(amountDigits),
        email: email.trim(),
        phoneNumber: phoneDigits ? toE164Display(phoneDigits) : undefined,
      });
      setSent(true);
    } catch (err: unknown) {
      console.warn('[ConnectPage] /leads failed:', err);
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong submitting your request. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.root}>
      <TopNav variant="back" />

      <Box component="main" sx={styles.main}>
        <Box sx={styles.card}>
          <Box component="form" sx={styles.formCol} onSubmit={handleSubmit} noValidate>
            <Typography component="h1" sx={styles.heading}>
              Let&rsquo;s put your gift to work.
            </Typography>
            <Typography sx={styles.intro}>
              Tell us where to send your proposal and 530A Solutions will be
              in touch to bring your impact to life. We will let you know once
              contribution payments are set up.
            </Typography>

            <Box sx={styles.nameRow}>
              <Box sx={styles.field}>
                <Typography component="label" sx={styles.label}>
                  First name<Box component="span" sx={styles.req}> *</Box>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => markTouched('firstName')}
                  error={!!showError('firstName')}
                  sx={styles.input}
                  size="small"
                  hiddenLabel
                />
                {showError('firstName') && (
                  <Typography sx={styles.errorText}>{errors.firstName}</Typography>
                )}
              </Box>
              <Box sx={styles.field}>
                <Typography component="label" sx={styles.label}>
                  Last name<Box component="span" sx={styles.req}> *</Box>
                </Typography>
                <TextField
                  variant="outlined"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => markTouched('lastName')}
                  error={!!showError('lastName')}
                  sx={styles.input}
                  size="small"
                  hiddenLabel
                />
                {showError('lastName') && (
                  <Typography sx={styles.errorText}>{errors.lastName}</Typography>
                )}
              </Box>
            </Box>

            <Box sx={styles.field}>
              <Typography component="label" sx={styles.label}>
                Email<Box component="span" sx={styles.req}> *</Box>
              </Typography>
              <TextField
                variant="outlined"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => markTouched('email')}
                error={!!showError('email')}
                sx={styles.input}
                size="small"
                hiddenLabel
              />
              {showError('email') && (
                <Typography sx={styles.errorText}>{errors.email}</Typography>
              )}
            </Box>

            <Box sx={styles.field}>
              <Typography component="label" sx={styles.label}>
                Phone number
              </Typography>
              <TextField
                variant="outlined"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(formatUsPhone(e.target.value))}
                onBlur={() => markTouched('phone')}
                error={!!showError('phone')}
                sx={styles.input}
                size="small"
                hiddenLabel
                slotProps={{
                  htmlInput: { inputMode: 'numeric', maxLength: 14 },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography component="span" sx={styles.phonePrefix}>
                          US
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {showError('phone') && (
                <Typography sx={styles.errorText}>{errors.phone}</Typography>
              )}
            </Box>

            <Box sx={styles.field}>
              <Typography component="label" sx={styles.label}>
                Gift amount
              </Typography>
              <TextField
                variant="outlined"
                placeholder="1,000,000"
                value={amount}
                onChange={(e) => setAmount(groupUsd(digitsOnly(e.target.value)))}
                onBlur={() => markTouched('amount')}
                error={!!showError('amount')}
                sx={styles.input}
                size="small"
                hiddenLabel
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
              {showError('amount') && (
                <Typography sx={styles.errorText}>{errors.amount}</Typography>
              )}
            </Box>

            <Box sx={styles.agreeRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      markTouched('agreed');
                    }}
                  />
                }
                label={
                  <Typography component="span" sx={styles.agreeLabel}>
                    You agree to our{' '}
                    <Link href="/privacy-policy" sx={styles.privacyLink}>
                      privacy policy
                    </Link>
                    .
                  </Typography>
                }
              />
              {showError('agreed') && (
                <Typography sx={styles.errorText}>{errors.agreed}</Typography>
              )}
            </Box>

            {submitError && (
              <Typography sx={styles.errorText}>{submitError}</Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={styles.submit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit and connect'}
            </Button>
          </Box>

          <Box sx={styles.media}>
            <Box component="img" src={heroMasked} alt="" sx={styles.mediaImg} />
          </Box>
        </Box>
      </Box>

      <Dialog open={sent} onClose={() => setSent(false)}>
        <Box sx={styles.sentDialog}>
          <Box sx={styles.sentIcon}>
            <CheckIcon fontSize="small" />
          </Box>
          <Typography component="h1" sx={styles.sentHeading}>
            Submit Successful
          </Typography>
          <Typography sx={styles.sentBody}>
            Your details were submitted successfully. Worker Solutions will be
            in touch to bring your impact to life.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => navigate(-1)}
            sx={styles.sentButton}
          >
            Back to search
          </Button>
        </Box>
      </Dialog>

      <SummaryBar {...summary} />
      <LegalDisclaimer />
    </Box>
  );
}
